import Markdown from 'markdown-to-jsx';
import { NewsLetter } from '../../models/auto-generated-strapi-types/types/components/NewsLetter';
import {
  ButtonText,
  ErrorText,
  InnerWrapper,
  MailField,
  MailFieldInput,
  NewsLetterLeft,
  NewsLetterRight,
  OuterWrapper,
  Privacy,
  PrivacyText,
  SubTitle,
  SubmitButton,
  Title,
} from './NewsLetter.style';
import { SchemaOf } from 'yup';
import * as Yup from 'yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { SnackbarActions, useSnackbar } from '../../stores/snackbar';
import { Logger } from 'corporate-utils';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import React from 'react';
import { getUseAlternativeCaptcha } from '../../utils/captcha';

interface NewsletterFormikValues {
  email: string;
  privacy: boolean;
}

type NewsletterValidationSchema = SchemaOf<NewsletterFormikValues>;

interface NewsLetterSectionProps {
  data: NewsLetter;
  translations: any;
  userCountry: string;
}

const NewsLetterSection: React.FC<NewsLetterSectionProps> = ({
  data,
  translations,
  userCountry,
}: NewsLetterSectionProps) => {
  const { dispatch } = useSnackbar();
  const section = 'newsletter';
  const initialValues = (): NewsletterFormikValues => ({
    email: '',
    privacy: false,
  });
  const hCaptchaRef = React.useRef<HCaptcha>(null);
  const useAlternativeCaptcha = getUseAlternativeCaptcha(userCountry);

  const validationSchema = (): NewsletterValidationSchema =>
    Yup.object({
      email: Yup.string()
        .required(translations?.pages?.newsletter?.requiredError)
        .email(translations?.pages?.newsletter?.invalidError),
      privacy: Yup.boolean()
        .required(translations?.pages?.newsletter?.requiredError)
        .oneOf([true], translations?.pages?.newsletter?.requiredError),
    });

  const hubspotBodyAdapter = (values: NewsletterFormikValues) => ({
    submittedAt: Date.now(),
    fields: [
      ...Object.entries(values)?.map(([key, value]) => ({
        name: translations?.pages?.newsletter?.hubspot[key],
        value,
      })),
    ],
  });
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <OuterWrapper color={data?.backgroundColor}>
      <InnerWrapper>
        {data?.mainTitle && (
          <NewsLetterLeft>
            <Title>
              <Markdown>{data?.mainTitle}</Markdown>
            </Title>
            {data?.subTitle && (
              <SubTitle>
                <Markdown>{data?.subTitle}</Markdown>
              </SubTitle>
            )}
          </NewsLetterLeft>
        )}
        <NewsLetterRight>
          <Formik
            initialValues={initialValues()}
            validationSchema={(): NewsletterValidationSchema =>
              validationSchema()
            }
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={async (
              values: NewsletterFormikValues,
              { resetForm }: FormikHelpers<NewsletterFormikValues>
            ) => {
              try {
                let token;

                if (useAlternativeCaptcha) {
                  token = await hCaptchaRef?.current
                    ?.execute({ async: true })
                    .then(({ response }) => {
                      return response;
                    })
                    .catch((err) => {
                      console.error(err);
                    });
                } else {
                  token = (await executeRecaptcha?.('newsletter')) || '';
                }

                const headers = new Headers();
                headers.set('Content-Type', 'application/json');
                const resp = await fetch('/api/newsletter', {
                  headers,
                  method: 'POST',
                  body: JSON.stringify({
                    data: hubspotBodyAdapter(values),
                    action: 'newsletter',
                    captchaToken: {
                      token,
                      alternativeCaptcha: useAlternativeCaptcha,
                    },
                  }),
                });
                const isSuccess = resp.status === 200;

                const data: {
                  message?: string;
                  inlineMessage?: string;
                } = await resp.json();

                dispatch({
                  type: SnackbarActions.SHOW,
                  success: isSuccess,
                  message: isSuccess
                    ? data?.inlineMessage?.replace(/(<([^>]+)>)/gi, '')
                    : data?.message || translations?.pages?.errors?.generic,
                });

                if (isSuccess) {
                  resetForm();
                }
              } catch (error) {
                const err = error as Error;
                Logger.error(err);
                dispatch({
                  type: SnackbarActions.SHOW,
                  success: false,
                  message: translations?.pages?.errors?.generic,
                });
              }
            }}
          >
            {(formikProps) => (
              <Form>
                <MailField>
                  <Field
                    inline
                    name="email"
                    section={section}
                    component={MailFieldInput}
                    placeholder={
                      translations?.pages?.newsletter?.emailPlaceholder
                    }
                    type="email"
                  />
                  <SubmitButton type="submit">
                    <ButtonText>
                      {translations?.pages?.newsletter?.submitButtonText}
                    </ButtonText>
                  </SubmitButton>
                  {useAlternativeCaptcha && (
                    <HCaptcha
                      sitekey={process.env.NEXT_PUBLIC_H_CAPTCHA_SITE_KEY || ''}
                      size="invisible"
                      ref={hCaptchaRef}
                    />
                  )}
                </MailField>
                {formikProps.errors.email && (
                  <ErrorText>{formikProps.errors.email}</ErrorText>
                )}
                <Privacy>
                  <Field
                    type="checkbox"
                    name="privacy"
                    id="privacyCheckbox"
                    section={section}
                  />
                  <PrivacyText>
                    {data.privacyText && (
                      <Markdown>{data.privacyText}</Markdown>
                    )}
                  </PrivacyText>
                </Privacy>
                {formikProps.errors.privacy && (
                  <ErrorText>{formikProps.errors.privacy}</ErrorText>
                )}
              </Form>
            )}
          </Formik>
        </NewsLetterRight>
      </InnerWrapper>
    </OuterWrapper>
  );
};

export default NewsLetterSection;
